import { useEffect, useState } from "react";
import { AdminModal } from "@/components/admin/shared/AdminModal";
import { CancelButton } from "@/components/admin/shared/PageHeader";
import type { PriceUpdateLastInfo } from "@/api/priceUpdate";

type Direction = "increase" | "decrease";

type Props = {
  open: boolean;
  onClose: () => void;
  currency: "EUR" | "USD";
  /** Number of records the change will be applied to. */
  recordCount: number;
  applying: boolean;
  error: string | null;
  onApply: (percent: number, direction: Direction) => void;
  /** Most recent bulk update for this currency (null/empty = never applied). */
  lastUpdate?: PriceUpdateLastInfo | null;
  /** True while the summary is still loading. */
  loadingSummary?: boolean;
  /** Dialog heading. Defaults to the PEMSERTER wording. */
  title?: string;
  /**
   * What the change touches, for the counted noun ("record"/"records" on the
   * PEMSERTER price list, "price"/"prices" on a per-element-type dataset).
   */
  recordNoun?: { one: string; many: string };
  /**
   * Sentence after the count in the description. Defaults to the PEMSERTER
   * wording, which mentions the derived Distributor Net Price.
   */
  descriptionSuffix?: string;
  /** Scope shown next to "Last price update". Defaults to the currency. */
  scopeLabel?: string;
};

/** Render an ISO timestamp as a readable local date-time. */
const formatWhen = (iso: string): string => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
};

/**
 * Bulk "apply a percentage" dialog, shared by every dataset that offers a bulk
 * price change. Prices are scaled by (1 ± %/100) server-side; the backend is
 * authoritative — same contract as the single-record save. Defaults reproduce
 * the PEMSERTER price-list wording; other datasets override the copy props.
 */
export const ChangeSalesPriceModal = ({
  open,
  onClose,
  currency,
  recordCount,
  applying,
  error,
  onApply,
  lastUpdate,
  loadingSummary,
  title,
  recordNoun = { one: "record", many: "records" },
  descriptionSuffix = "Distributor Net Price will also update automatically.",
  scopeLabel,
}: Props) => {
  const [percentInput, setPercentInput] = useState("");
  const [direction, setDirection] = useState<Direction>("increase");

  // Reset to a clean state every time the dialog opens.
  useEffect(() => {
    if (open) {
      setPercentInput("");
      setDirection("increase");
    }
  }, [open]);

  const percent = Number(percentInput);
  const valid =
    percentInput.trim() !== "" && Number.isFinite(percent) && percent > 0;
  const signed = valid
    ? `${direction === "increase" ? "+" : "−"}${percent.toFixed(2)}%`
    : "";
  const noun = recordCount === 1 ? recordNoun.one : recordNoun.many;
  const records = `${recordCount} ${noun}`;
  const scope = scopeLabel ?? currency;

  const handleApply = () => {
    if (!valid || applying) return;
    onApply(percent, direction);
  };

  return (
    <AdminModal
      open={open}
      onClose={onClose}
      title={title ?? `Change Customer Sales Price (${currency})`}
      maxWidth="max-w-xl"
      footer={
        <>
          <span className="mr-auto self-center text-blue_grey3 text-small">
            {valid
              ? `${records} will be updated (${signed})`
              : "Enter a % and choose increase or decrease"}
          </span>
          <CancelButton onClick={onClose} />
          <button
            type="button"
            onClick={handleApply}
            disabled={!valid || applying}
            className="inline-flex items-center gap-2 whitespace-nowrap px-4 py-2 bg-penn-500 hover:bg-penn-700 text-white rounded-lg shadow-sm shadow-penn-100 dark:shadow-penn-900/30 transition-all duration-150 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer text-body-medium"
          >
            {applying ? "Applying…" : `Apply to All${valid ? ` (${signed})` : ""}`}
          </button>
        </>
      }
    >
      <div className="space-y-4">
        <p className="text-blue_grey3 text-body">
          Apply a percentage change to recalculate all sales prices across{" "}
          <span className="font-semibold text-blue_grey">
            {recordCount}
          </span>{" "}
          {noun}. {descriptionSuffix}
        </p>

        {/* Last bulk update for this currency. */}
        {loadingSummary ? (
          <p className="text-blue_grey4 text-small">
            Loading last update…
          </p>
        ) : lastUpdate?.lastUpdatedAt ? (
          <div className="rounded-lg border border-gray-200 dark:border-gray-800 bg-gray-50/60 dark:bg-gray-800/30 px-3 py-3 text-blue_grey2 space-y-1 text-small">
            <p className="font-medium text-blue_grey">
              Last price update ({scope})
            </p>
            <div className="flex flex-wrap gap-x-4 gap-y-1">
              <span>
                <span className="text-blue_grey4">Change:</span>{" "}
                <span
                  className={
                    lastUpdate.lastDirection === "increase"
                      ? "font-medium text-green-600 dark:text-green-400"
                      : "font-medium text-red-600 dark:text-red-400"
                  }
                >
                  {lastUpdate.lastDirection === "increase" ? "+" : "−"}
                  {lastUpdate.lastPercentage}%
                </span>
              </span>
              <span>
                <span className="text-blue_grey4">When:</span>{" "}
                {formatWhen(lastUpdate.lastUpdatedAt)}
              </span>
              {lastUpdate.lastUpdatedBy && (
                <span>
                  <span className="text-blue_grey4">By:</span>{" "}
                  {lastUpdate.lastUpdatedBy}
                </span>
              )}
            </div>
          </div>
        ) : (
          <p className="text-blue_grey4 text-small">
            No previous bulk price update for {scope}.
          </p>
        )}

        {error && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-red-700 text-body">
            {error}
          </div>
        )}

        <div className="rounded-2xl border border-gray-200 dark:border-gray-800 p-4 space-y-3">
          <label
            htmlFor="percentChange"
            className="block text-blue_grey text-body-medium"
          >
            Percentage Change <span className="text-red-500">*</span>
          </label>

          <div className="relative">
            <input
              id="percentChange"
              type="number"
              min={0}
              step="0.01"
              value={percentInput}
              onChange={(e) => setPercentInput(e.target.value)}
              placeholder="e.g. 20"
              className="w-full rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-3 pr-9 text-blue_grey placeholder-gray-400 focus:border-penn-500 focus:ring-1 focus:ring-penn-500 outline-none text-body"
            />
            <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-penn-500 text-body-medium">
              %
            </span>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => setDirection("increase")}
              className={`inline-flex items-center justify-center gap-2 rounded-lg border px-4 py-3 text-body-medium transition-colors cursor-pointer ${
                direction === "increase"
                  ? "border-green-400 bg-green-50 text-green-700 ring-1 ring-green-300 dark:bg-green-900/30 dark:text-green-300"
                  : "border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-blue_grey2 hover:bg-gray-50 dark:hover:bg-gray-700"
              }`}
            >
              ▲ Increase
            </button>
            <button
              type="button"
              onClick={() => setDirection("decrease")}
              className={`inline-flex items-center justify-center gap-2 rounded-lg border px-4 py-3 text-body-medium transition-colors cursor-pointer ${
                direction === "decrease"
                  ? "border-red-400 bg-red-50 text-red-700 ring-1 ring-red-300 dark:bg-red-900/30 dark:text-red-300"
                  : "border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-blue_grey2 hover:bg-gray-50 dark:hover:bg-gray-700"
              }`}
            >
              ▼ Decrease
            </button>
          </div>

          <div className="space-y-1 text-blue_grey4 text-small">
            <p>Enter a % value and choose Increase or Decrease.</p>
            <p>
              Formula: New Price = Current Price × (1 + %/100) or × (1 − %/100)
            </p>
          </div>
        </div>
      </div>
    </AdminModal>
  );
};
